import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import { IRuleGroup } from "../../model/evaluation/RuleGroup";
import GetRuleGroupsInteractor from "../../interactor/evaluation/GetRuleGroupsInteractor";
import CreateRuleGroupInteractor from "../../interactor/evaluation/CreateRuleGroupInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import UpdateRuleGroupInteractor from "../../interactor/evaluation/UpdateRuleGroupInteractor";
import { IRuleGroupElement } from "../../model/evaluation/RuleGroupElement";
import GetBusinessRulesInteractor from "../../interactor/evaluation/GetBusinessRulesInteractor";
import GetRuleGroupElementsInteractor from "../../interactor/evaluation/GetRuleGroupElementsInteractor";
import { IBusinessRule } from "../../model/evaluation/BusinessRule";
import RemoveBusinessRuleFromRuleGroupInteractor from "../../interactor/evaluation/RemoveBusinessRuleFromRuleGroupInteractor";
import AddBusinessRuleToRuleGroupInteractor from "../../interactor/evaluation/AddBusinessRuleToRuleGroupInteractor";
import UpdateBusinessRuleInRuleGroupInteractor from "../../interactor/evaluation/UpdateBusinessRuleInRuleGroupInteractor";
import { TRuleGroupPresentationModel } from "./model/RuleGroupPresentationModel";

export interface TRuleGroupsPresenter extends TLoadingAwarePresenter {
  ruleGroups: IRuleGroup[] | undefined;
  ruleGroupElements?: IRuleGroupElementPresentationModel[];
  selectedBusinessRuleIds?: string[];
}
export interface IRuleGroupsPresenter
  extends TRuleGroupsPresenter,
    TPresentable {
  getRuleGroups(): Promise<void>;
  setRuleGroups(ruleGroups: IRuleGroup[]): void;
  createRuleGroup(ruleGroup: TRuleGroupPresentationModel): void;
  updateRuleGroup(ruleGroup: TRuleGroupPresentationModel): void;
  getRuleGroupElements(ruleGroup: IRuleGroup): Promise<any>;
  selectRuleGroupElements(
    ruleGroup?: IRuleGroup,
    elements?: IRuleGroupElementPresentationModel[],
    selectedBusinessRuleIds?: string[]
  ): void;
}

const defaultState: TRuleGroupsPresenter = {
  ruleGroups: [],
  ruleGroupElements: [],
  selectedBusinessRuleIds: []
};

export interface IRuleGroupElementPresentationModel {
  rule: IBusinessRule;
  element?: IRuleGroupElement;
  selected: boolean;
}
const RuleGroupElementPresentationModel = (
  element: IRuleGroupElement | undefined,
  rule: IBusinessRule,
  selected: boolean
) => {
  return {
    rule,
    element,
    selected
  };
};

const RuleGroupsPresenter = withStore<
  IRuleGroupsPresenter,
  TRuleGroupsPresenter
>(({ application, store, loader, translate }): IRuleGroupsPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TRuleGroupsPresenter>();

  loader.start("ruleGroupsLoader");

  const setRuleGroups = (ruleGroups: IRuleGroup[]) => {
    _store.update({ ruleGroups });
    loader.stop("ruleGroupsLoader");
  };

  const selectRuleGroupElements = (
    ruleGroup: IRuleGroup,
    elements: IRuleGroupElementPresentationModel[],
    selectedBusinessRuleIds: string[]
  ) => {
    _store.update({ ruleGroupElements: elements, selectedBusinessRuleIds });
  };

  const getRuleGroupElements = async (ruleGroup: IRuleGroup) => {
    try {
      const allRules = await _application.container
        .resolve<GetBusinessRulesInteractor>("getBusinessRules")
        .execute();
      let groupRules: IRuleGroupElement[] = [];
      if (ruleGroup && ruleGroup.id) {
        groupRules = await _application.container
          .resolve<GetRuleGroupElementsInteractor>("getRuleGroupElements")
          .execute(ruleGroup.id);
      }

      const selectedRules = allRules.map((item) => {
        const existingRule = groupRules.find(
          (rule) => rule.businessRule.id === item.id
        );
        if (existingRule) {
          return RuleGroupElementPresentationModel(existingRule, item, true);
        }
        return RuleGroupElementPresentationModel(undefined, item, false);
      });

      return {
        get: selectedRules,
        update: selectRuleGroupElements
      };
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
      return {
        get: []
      };
    }
  };

  const getRuleGroups = async () => {
    try {
      let ruleGroups = await _application.container
        .resolve<GetRuleGroupsInteractor>("getRuleGroups")
        .execute();
      _store.update({ ruleGroups });
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const createRuleGroup = async (ruleGroup: TRuleGroupPresentationModel) => {
    try {
      _store.update({
        ruleGroupElements: ruleGroup.ruleGroupElements,
        selectedBusinessRuleIds: ruleGroup.selectedElementsIds
      });
      const newRuleGroupId = await _application.container
        .resolve<CreateRuleGroupInteractor>("createRuleGroup")
        .execute(ruleGroup as IRuleGroup);
      (ruleGroup as IRuleGroup).id = newRuleGroupId;
      await updateRuleGroupElements(ruleGroup as IRuleGroup);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("ruleGroups_page.messages.create_ruleGroup_success")
        );
      await getRuleGroups();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };
  const updateRuleGroupElements = async (ruleGroup: IRuleGroup) => {
    const elements = _store.getState<TRuleGroupsPresenter>().ruleGroupElements;
    const selectedBusinessRuleIds =
      _store.getState<TRuleGroupsPresenter>().selectedBusinessRuleIds;
    let promises =
      elements &&
      elements
        .filter((item) => item.selected)
        .map(async (item) => {
          if (
            selectedBusinessRuleIds &&
            selectedBusinessRuleIds.find((id) => id === item.rule.id.toString())
          ) {
            await _application.container
              .resolve<UpdateBusinessRuleInRuleGroupInteractor>(
                "updateBusinessRuleInRuleGroup"
              )
              .execute(ruleGroup, item.element as IRuleGroupElement);
            return Promise.resolve({});
          } else {
            return _application.container
              .resolve<RemoveBusinessRuleFromRuleGroupInteractor>(
                "removeBusinessRuleFromRuleGroup"
              )
              .execute(ruleGroup, item.element as IRuleGroupElement);
          }
        });
    promises = [
      ...promises,
      ...(selectedBusinessRuleIds &&
        selectedBusinessRuleIds.map((item) => {
          let foundElement =
            elements &&
            elements
              .filter((item) => !item.selected)
              .find((element) => element.rule.id.toString() === item);
          if (!foundElement) {
            return Promise.resolve({});
          } else {
            let element = {} as IRuleGroupElement;
            element.businessRule = foundElement.rule;
            element.weightFactor = foundElement.element?.weightFactor;
            element.displayNumber = foundElement.element?.displayNumber;
            return _application.container
              .resolve<AddBusinessRuleToRuleGroupInteractor>(
                "addBusinessRuleToRuleGroup"
              )
              .execute(ruleGroup, element as IRuleGroupElement);
          }
        }))
    ];

    return Promise.all(promises);
  };

  const updateRuleGroup = async (ruleGroup: TRuleGroupPresentationModel) => {
    try {
      _store.update({
        ruleGroupElements: ruleGroup.ruleGroupElements,
        selectedBusinessRuleIds: ruleGroup.selectedElementsIds
      });
      await _application.container
        .resolve<UpdateRuleGroupInteractor>("updateRuleGroup")
        .execute(ruleGroup as IRuleGroup);
      await updateRuleGroupElements(ruleGroup as IRuleGroup);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("ruleGroups_page.messages.update_ruleGroup_success")
        );
      await getRuleGroups();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  return {
    ...state,

    store: _store,
    loader,
    application: _application,
    translate,
    getRuleGroups,
    setRuleGroups,
    createRuleGroup,
    updateRuleGroup,
    getRuleGroupElements,
    selectRuleGroupElements
  };
}, defaultState);

export default RuleGroupsPresenter;
